/**
 * ============================================================================
 * KbSpacesPage — Listagem e criação de Spaces da Base de Conhecimento
 * ----------------------------------------------------------------------------
 * F1 Sprint 2 · Bloco 3/3
 * CHANGELOG:
 *   RELEASE: 2026-07-07 v1.0.0 - Versão inicial (grid de spaces + dialog criar).
 * ============================================================================
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, BookOpen, Search, Upload, ShieldCheck, Users } from "lucide-react";
import { useKbSpaces, useCreateKbSpace } from "@/hooks/kb/useKb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

// Utilitário: gera slug a partir do nome
const toSlug = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

export default function KbSpacesPage() {
  const { data: spaces = [], isLoading } = useKbSpaces();
  const createSpace = useCreateKbSpace();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [search, setSearch] = useState("");

  // Filtro local por nome
  const filtered = spaces.filter((s: any) =>
    !search || s.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async () => {
    if (!name.trim()) return;
    await createSpace.mutateAsync({
      name: name.trim(),
      slug: toSlug(name),
      description: description.trim() || null,
    });
    setName(""); setDescription(""); setOpen(false);
  };

  return (
    <div className="flex h-full flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Base de Conhecimento</h1>
          <p className="text-sm text-muted-foreground">
            Organize spaces, coleções e páginas da empresa.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/tenant/kb/permissions">
              <ShieldCheck className="h-4 w-4 mr-1" /> Acessos por perfil
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/tenant/kb/extranet">
              <Users className="h-4 w-4 mr-1" /> Extranet
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/tenant/kb/import">
              <Upload className="h-4 w-4 mr-1" /> Importar em lote
            </Link>
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-1" /> Novo space
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Criar space</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nome</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={500} />
              </div>
              <p className="text-xs text-muted-foreground">
                Slug: <code>{toSlug(name) || "—"}</code>
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={!name.trim() || createSpace.isPending}>
                {createSpace.isPending ? "Criando..." : "Criar"}
              </Button>
            </DialogFooter>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Busca */}
      <div className="relative max-w-md">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar spaces..."
          className="pl-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Grid de spaces */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-lg" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="flex flex-col items-center justify-center p-10 text-center">
          <BookOpen className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            {spaces.length === 0 ? "Nenhum space criado ainda." : "Nenhum space encontrado."}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((s: any) => (
            <Link key={s.id} to={`/tenant/kb/${s.id}`}>
              <Card className="p-4 hover:border-primary transition-colors h-full">
                <div className="flex items-start gap-3">
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-lg"
                    style={{ backgroundColor: s.color ?? "hsl(var(--muted))" }}
                  >
                    {s.icon ?? "📚"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{s.name}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2">
                      {s.description ?? "Sem descrição"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {s.pages_count} página{Number(s.pages_count) === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
